import { useContext } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { ProductsContext } from 'context-providers/products-provider';
import {
  benefitsTestHero,
  benefitsTestHeroPill,
  ds01QuizHero,
} from './content/hero.content';
import { UgcHeadline, items } from './content/ugc.content';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import {
  benefitsTestCapsuleLarge,
  benefitsTestCapsuleSmall,
  benefitsTestNoteTitle,
  benefitsTestPrimaryButton,
  capsuleLarge,
  capsuleSmall,
} from './content/via-cap.content';
import {
  benefitsTestBookendItems,
  bookendItems,
} from './content/bookend.content';
import { content } from './content/via-cap.content';
import {
  microbiomeChapters,
  microbiomeImageSlide,
} from './content/micro-biome.content';
import { npdProductContent } from './content/product-carousel.content';
import {
  videos,
  ReviewCarouselHeadline,
} from './content/review-carousel.content';
import Bookends from 'components/library/Bookend';
import Loader from 'components/library/Loader';
import { UgcScroller } from 'components/library/Ugc';

import ViaCap from 'components/library/via-cap';
import ReviewCarousel from 'components/library/ReviewCarousel';
import Microbiome from 'components/library/MicroBiome';
import Head from 'next/head';
import HomeLocale from 'locale/en/home.json';
import ProductGrid from 'components/library/ProductCarousel/product-grid';
import Highlight from 'components/library/Highlight';
import { HighlightBackground } from 'components/library/Highlight/highlight.styled';
import { getMetadataTags } from 'lib/utils/get-metadata-tags';
import { dsDuoItems } from 'components/library/Highlight/highlight.content';
import { useActiveMiniSale } from 'hooks/use-active-mini-sale';
import Badge from 'components/global/Badge';
import Benefits from 'components/home/Benefits';
import ProductLineup from 'components/home/ProductLineup';
import {
  productLineupBody,
  productLineupHeading,
  productLineupItems,
  productLineupShopAll,
} from './content/product-lineup.content';
import { benefitsHeading, benefitsItems } from './content/benefits.content';
import { withFootnotes } from './footnotes';
import { formatTrademarks, TrademarkText } from 'lib/utils/format-trademarks';
import {
  HomepageVariantSlot,
  HomepageExperimentStyles,
  useHomepageVariant,
} from './homepage-experiment';

const DS01_CAROUSEL_URL = '/daily-synbiotic';

const GlobalHomepageStyles = createGlobalStyle`
  main {
    overflow: visible !important;
    margin-top: var(--flag-margin);
  }

  /* White-theme nav over the dark hero */
  body {
    --nav-ink: var(--fill-primary);
    --nav-logo: var(--fill-bright);
    --nav-cta-background: var(--fill-primary);
    --nav-cta-ink: var(--fill-brand);
  }
`;

const HeroPill = styled.div`
  display: flex;
`;

const PageData = {
  page_type: 'default',
  page_class: 'page-home',
  page_name: HomeLocale.page_name,
  page_title: HomeLocale.page_title,
  page_description: HomeLocale.page_desc,
};

const Home = () => {
  const isSmall = useClientSideMediaQuery(960);
  const miniSale = useActiveMiniSale();
  const { getFormattedProductPrice } = useContext(ProductsContext);
  const activeVariant = useHomepageVariant();

  const carouselItems = npdProductContent.items.map((item) => {
    const price = item.sku ? getFormattedProductPrice(item.sku) : undefined;
    return {
      ...item,
      ...(price && {
        note: `Starting at ${price}`,
        desktopNote: `Starting at ${price} per month`,
      }),
      ...(miniSale &&
        item.primaryButton.url === DS01_CAROUSEL_URL && {
          label: miniSale.saleBadgeText,
        }),
    };
  });

  const renderDuo = (
    id: string,
    variant: 'left' | 'right',
    borderRadius?: string,
  ) => (
    <HighlightBackground id={id}>
      <Highlight
        borderRadius={borderRadius}
        label={'Bundle + Save 25%'}
        title={'Daily essentials for nutrition and digestive health.'}
        body={
          'Our clinically studied daily synbiotic paired with a daily multivitamin reduces bloating, promotes healthy regularity and helps cover nutrient gaps.'
        }
        primaryButton={{
          label: 'Shop Daily Essentials Duo',
          url: '/daily-essentials-duo',
        }}
        items={dsDuoItems}
        variant={variant}
        applyTransform={false}
        blockSize='auto'
      />
    </HighlightBackground>
  );

  const viaCap = (
    <ViaCap
      eyebrow={content.eyebrow}
      description={content.description}
      background={content.background}
      backgroundVideo={content.backgroundVideo}
      note={content.note}
      footnote={content.footnote}
      capsule={isSmall ? capsuleSmall : capsuleLarge}
    />
  );

  return (
    <>
      <GlobalHomepageStyles />
      <HomepageExperimentStyles />
      <Head>
        <title>{PageData.page_title}</title>
        {getMetadataTags({
          pageDescription: PageData.page_description,
          canonicalUrl: 'https://seed.com',
          pageTitle: PageData.page_title,
          imageUrl:
            'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517361/structured-data/og-homepage.png',
          ogImageAlt: PageData.page_title,
        })}
      </Head>
      <HomepageVariantSlot variant='control' activeVariant={activeVariant}>
        <Loader slides={ds01QuizHero} id={'homepage-ds01-hero'} />
      </HomepageVariantSlot>
      <HomepageVariantSlot variant='variant_1' activeVariant={activeVariant}>
        <Loader
          slides={benefitsTestHero}
          id={'homepage-ds01-hero-variant'}
          config={{ backgroundColor: 'var(--fill-tertiary)' }}
          pillAndTitle={
            <HeroPill>
              <Badge color='guidance-primary' size='lg'>
                <TrademarkText>
                  {formatTrademarks(benefitsTestHeroPill, true)}
                </TrademarkText>
              </Badge>
            </HeroPill>
          }
        />
      </HomepageVariantSlot>

      <HomepageVariantSlot variant='control' activeVariant={activeVariant}>
        <ProductGrid
          id={'homepage-product-carousel-control'}
          title={npdProductContent.title}
          body={npdProductContent.body}
          items={carouselItems}
          primaryButton={npdProductContent.primaryButton}
          align={'left'}
        />
        {renderDuo(
          'homepage-product-highlight-control',
          'right',
          'var(--radius-xl) var(--radius-xl) 0 0',
        )}
        {viaCap}
      </HomepageVariantSlot>
      <HomepageVariantSlot variant='variant_1' activeVariant={activeVariant}>
        <Benefits
          id='homepage-benefits'
          heading={benefitsHeading}
          items={benefitsItems}
        />
        <ViaCap
          eyebrow={content.eyebrow}
          description={content.description}
          background={content.background}
          backgroundVideo={content.backgroundVideo}
          note={{
            ...content.note,
            title: withFootnotes(benefitsTestNoteTitle),
          }}
          primaryButton={benefitsTestPrimaryButton}
          layout='split'
          capsule={
            isSmall ? benefitsTestCapsuleSmall : benefitsTestCapsuleLarge
          }
        />
        <ProductLineup
          id='homepage-product-lineup'
          heading={productLineupHeading}
          body={productLineupBody}
          shopAll={productLineupShopAll}
          items={productLineupItems}
        />
      </HomepageVariantSlot>

      <Microbiome chapters={[...microbiomeChapters, microbiomeImageSlide]} />

      <HomepageVariantSlot variant='variant_1' activeVariant={activeVariant}>
        {renderDuo('homepage-product-highlight-variant', 'left')}
      </HomepageVariantSlot>

      <ReviewCarousel headline={<ReviewCarouselHeadline />} videos={videos} />
      <UgcScroller headline={<UgcHeadline />} items={items} type='simple' />

      <Bookends
        items={
          activeVariant === 'variant_1'
            ? benefitsTestBookendItems
            : bookendItems
        }
      />
    </>
  );
};

export default Home;
